import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  EditOutlined as EditIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { AgronodTypography } from '@agronod/mui-components';
import { Tooltip } from '@agronod/mui-components';

import type { FilterCondition } from '@/types/farm-profile';
import {
  FILTER_FIELDS,
  FIELD_OPERATORS,
  defaultOperatorFor,
  defaultValueFor,
  defaultValueForOperator,
  operatorLabel,
  valueInputKindFor,
} from '@/constants/farm-profile/filter-rule';
import ChipSelectButton from '@/components/farm-profile/steps/ChipSelectButton';
import { deriveConditionReason } from '@/utils/farm-profile/deriveReason';
import { describeRule } from '@/utils/farm-profile/describeRule';

interface FilterConditionRowProps {
  /** Current condition value. */
  value: FilterCondition;
  /** Called whenever any field in this condition changes. */
  onChange: (next: FilterCondition) => void;
  /** Called when the delete button is clicked. */
  onRemove: () => void;
}

/**
 * FilterConditionRow — renders a single filter condition editor.
 *
 * Layout (left to right inside a Card):
 *   [Field Select] [Operator Select] [Value Input] [Reason TextField] [Delete]
 *
 * When `field` changes, `operator` and `value` are reset to sensible defaults
 * for the new field's domain. When `operator` changes and the value input
 * changes kind (e.g. Ekostatus `eq` → `includesAny`), `value` is reset to the
 * empty value of the new kind so the condition always stays coherent.
 *
 * Value input is decided by operator first, then domain (`valueInputKindFor`):
 * - set operators → chip grid (ChipSelectButton toggles membership in value[]),
 *   also on the scalar Ekostatus field where the list means "one of these"
 * - `number` domain → TextField type="number"
 * - `single` domain with `eq` → Select with the field's options
 * - operator `isEmpty` → no value input (value is irrelevant for this operator)
 *
 * @param value    The current FilterCondition state.
 * @param onChange Callback with updated condition.
 * @param onRemove Callback when this condition should be removed.
 */
const FilterConditionRow: React.FC<FilterConditionRowProps> = ({
  value,
  onChange,
  onRemove,
}) => {
  const fieldDef = FILTER_FIELDS.find((f) => f.key === value.field);

  // Reason input is optional (auto-generated). Open it only when admin already
  // wrote something, otherwise keep the row calm behind a toggle.
  const hasCustomReason = !!(value.reason && value.reason.trim().length > 0);
  const [reasonOpen, setReasonOpen] = useState(hasCustomReason);

  // Per-row plain-language echo: "har någon av KRAV" etc.
  const clause = describeRule({ conditions: [value] }).shownFor;

  // A stored condition on a field the editor no longer offers (combinatorBusiness
  // since AG-274). The selects would render blank with no explanation, so say
  // what the condition was and leave only the delete action.
  if (!fieldDef) {
    return (
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Stack direction="row" spacing={1.5} sx={{
            alignItems: "center"
          }}>
            <AgronodTypography
              variant="body2"
              sx={{
                color: "text.secondary",
                flexGrow: 1
              }}>
              Villkoret "visas för gårdar som {clause}" gäller en egenskap som inte längre
              finns i gårdsprofilen och kan inte redigeras. Ta bort det.
            </AgronodTypography>
            <Tooltip title="Ta bort detta villkor">
              <IconButton
                onClick={onRemove}
                color="error"
                size="small"
                aria-label="Ta bort villkor"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const operators = FIELD_OPERATORS[fieldDef.domain];
  const inputKind = valueInputKindFor(fieldDef.domain, value.operator);

  const handleFieldChange = (nextField: string) => {
    // Casting is safe: nextField comes from a MenuItem whose values are FILTER_FIELDS keys.
    const field = nextField as FilterCondition['field'];
    onChange({
      ...value,
      field,
      operator: defaultOperatorFor(field),
      value: defaultValueFor(field),
    });
  };

  const handleOperatorChange = (nextOperator: string) => {
    const operator = nextOperator as FilterCondition['operator'];
    const nextKind = valueInputKindFor(fieldDef.domain, operator);
    // A select value ('Ekologisk') is not a valid chips value and vice versa;
    // reset only when the input kind actually changes so a chips → chips
    // switch (includesAny → excludesAny) keeps the admin's selection.
    const keepValue = nextKind === inputKind;
    onChange({
      ...value,
      operator,
      ...(keepValue ? {} : { value: defaultValueForOperator(value.field, operator) }),
    });
  };

  const handleValueChange = (nextValue: FilterCondition['value']) => {
    onChange({ ...value, value: nextValue });
  };

  const toggleMultiValue = (option: string) => {
    const current = Array.isArray(value.value) ? (value.value as string[]) : [];
    const next = current.includes(option)
      ? current.filter((v) => v !== option)
      : [...current, option];
    handleValueChange(next);
  };

  const renderValueInput = () => {
    switch (inputKind) {
      case 'none':
        return null;

      case 'chips': {
        if (!fieldDef.options) return null;
        const selected = Array.isArray(value.value) ? (value.value as string[]) : [];
        return (
          <Box sx={{ flex: 1 }}>
            <AgronodTypography
              variant="caption"
              sx={{
                color: "text.secondary",
                mb: 0.5,
                display: 'block'
              }}>
              Välj värden
            </AgronodTypography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {fieldDef.options.map((option) => (
                <ChipSelectButton
                  key={option}
                  label={option}
                  checked={selected.includes(option)}
                  onClick={() => toggleMultiValue(option)}
                />
              ))}
            </Box>
          </Box>
        );
      }

      case 'number':
        return (
          <TextField
            label="Värde"
            type="number"
            size="small"
            value={typeof value.value === 'number' ? value.value : 0}
            onChange={(e) => handleValueChange(Number(e.target.value))}
            sx={{ width: 140 }}
            slotProps={{
              htmlInput: { step: 1, min: 0 }
            }}
          />
        );

      case 'select':
        if (!fieldDef.options) return null;
        return (
          <TextField
            select
            label="Värde"
            size="small"
            value={typeof value.value === 'string' ? value.value : ''}
            onChange={(e) => handleValueChange(e.target.value)}
            sx={{ width: 180 }}
          >
            {fieldDef.options.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );
    }
  };

  const inlineInput = inputKind === 'number' || inputKind === 'select';

  return (
    <Card variant="outlined" sx={{ mb: 1.5 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1.5}>
          {/* Field + Operator row */}
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: "flex-start",
              flexWrap: "wrap"
            }}>
            <AgronodTypography
              variant="body2"
              sx={{
                color: "text.secondary",
                pt: 1,
                flexShrink: 0
              }}>
              Gårdens
            </AgronodTypography>

            <TextField
              select
              label="egenskap"
              size="small"
              value={value.field}
              onChange={(e) => handleFieldChange(e.target.value)}
              sx={{ minWidth: 190 }}
            >
              {FILTER_FIELDS.map((f) => (
                <MenuItem key={f.key} value={f.key}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="villkor"
              size="small"
              value={value.operator}
              onChange={(e) => handleOperatorChange(e.target.value)}
              sx={{ minWidth: 150 }}
            >
              {operators.map((op) => (
                <MenuItem key={op} value={op}>
                  {operatorLabel(fieldDef.domain, op)}
                </MenuItem>
              ))}
            </TextField>

            {/* Value input: shown inline for number/select, pushed to next line for chips */}
            {inlineInput && renderValueInput()}

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title="Ta bort detta villkor">
              <IconButton
                onClick={onRemove}
                color="error"
                size="small"
                aria-label="Ta bort villkor"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Chip grid on its own row */}
          {inputKind === 'chips' && <Box>{renderValueInput()}</Box>}

          {/* Per-row plain-language echo */}
          <Stack direction="row" spacing={0.75} sx={{
            alignItems: "center"
          }}>
            <ArrowIcon sx={{ fontSize: 15, color: 'success.main', flexShrink: 0 }} />
            <AgronodTypography variant="caption" sx={{
              color: "text.secondary"
            }}>
              Visas för gårdar som {clause}
            </AgronodTypography>
          </Stack>

          {/* Reason — optional, collapsed behind a toggle. Auto-generated when empty. */}
          <Box>
            <Button
              variant="text"
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 15 }} />}
              onClick={() => setReasonOpen((v) => !v)}
              sx={{ color: 'text.secondary', fontSize: '0.75rem', px: 0.5 }}
            >
              {reasonOpen ? 'Dölj meddelandet' : 'Anpassa meddelandet till lantbrukaren'}
            </Button>
            <Collapse in={reasonOpen} timeout={200}>
              <TextField
                label="Meddelande till lantbrukaren"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={value.reason ?? ''}
                onChange={(e) => onChange({ ...value, reason: e.target.value })}
                placeholder={deriveConditionReason(value)}
                helperText={
                  hasCustomReason
                    ? `${value.reason!.length}/500 tecken`
                    : `Lämnas tom: "${deriveConditionReason(value)}" visas automatiskt`
                }
                slotProps={{ htmlInput: { maxLength: 500 } }}
                sx={{ mt: 1 }}
              />
            </Collapse>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default FilterConditionRow;
